import { Info, Upload, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { LogoSource } from '@/types/template-builder';

type LogoSize = 'small' | 'medium' | 'large';

interface BrandingTabProps {
  companyName: string;
  companyLogoUrl?: string;
  currentLogo?: string | null;
  logoSource: LogoSource;
  onLogoChange: (logoUrl: string | null, source: LogoSource) => void;
  onSizeChange: (size: LogoSize) => void;
  logoSize: LogoSize;
  primaryColor: string;
  documentFont: string;
  onPrimaryColorChange: (color: string) => void;
  onDocumentFontChange: (font: string) => void;
}

export function BrandingTab({
  companyName,
  companyLogoUrl,
  currentLogo,
  logoSource,
  onLogoChange,
  onSizeChange,
  logoSize,
  primaryColor,
  documentFont,
  onPrimaryColorChange,
  onDocumentFontChange,
}: Readonly<BrandingTabProps>) {
  const initialCustomLogo = logoSource === 'custom' && currentLogo ? currentLogo : null;
  const [customLogo, setCustomLogo] = useState<string | null>(initialCustomLogo);
  const [showUpload, setShowUpload] = useState(!companyLogoUrl && !initialCustomLogo && logoSource !== 'none');
  const [isCleared, setIsCleared] = useState(logoSource === 'none');
  const [isDragging, setIsDragging] = useState(false);

  // Sync internal state when parent updates logoSource/companyLogoUrl asynchronously
  useEffect(() => {
    if (logoSource === 'company' && companyLogoUrl) {
      setIsCleared(false);
      setCustomLogo(null);
      setShowUpload(false);
    } else if (logoSource === 'none') {
      setIsCleared(true);
    }
  }, [logoSource, companyLogoUrl]);

  const handleFileUpload = (file: File) => {
    const validTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
    ];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (PNG or JPG)');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('File size must be less than 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setCustomLogo(dataUrl);
      setIsCleared(false);
      onLogoChange(dataUrl, 'custom');
      setShowUpload(false);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleClearLogo = () => {
    setCustomLogo(null);
    setIsCleared(true);
    onLogoChange(null, 'none');
    setShowUpload(false);
  };

  const handleRevertToCompanyLogo = () => {
    setCustomLogo(null);
    setIsCleared(false);
    onLogoChange(null, 'company');
    setShowUpload(false);
  };

  const handleUseDifferentLogo = () => setShowUpload(true);

  return (
    <div className="space-y-4 p-4">
      {/* Panel 1: Company Logo */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Company Logo</CardTitle>
          <CardDescription>
            Your logo will appear in the header of your statement document.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* State A - Company has a logo */}
          {companyLogoUrl && !showUpload && !customLogo && !isCleared && (
            <div className="space-y-3">
              <div className="relative border rounded-lg p-6 bg-white flex flex-col items-center justify-center">
                <button
                  onClick={handleClearLogo}
                  className="absolute top-2 right-2 h-6 w-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  title="Clear logo"
                >
                  <X className="h-4 w-4" />
                </button>
                <img
                  src={companyLogoUrl}
                  alt={`${companyName} logo`}
                  className="max-h-20 max-w-60 object-contain"
                />
                <p className="text-xs text-muted-foreground mt-3">
                  Logo from {companyName}
                </p>
              </div>
              <button
                onClick={handleUseDifferentLogo}
                className="text-sm text-primary hover:underline"
              >
                Use a different logo for this template →
              </button>
            </div>
          )}

          {/* Cleared State - Logo explicitly removed */}
          {isCleared && !customLogo && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 rounded-md bg-muted border">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Logo has been removed. The statement will be generated without a logo.
                </p>
              </div>
              {companyLogoUrl && (
                <button
                  onClick={handleRevertToCompanyLogo}
                  className="text-sm text-primary hover:underline"
                >
                  Restore company logo →
                </button>
              )}
              <div className="space-y-2">
                <Label className="text-sm">
                  Or upload a logo for this template
                </Label>
                <UploadComponent
                  isDragging={isDragging}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onFileSelect={handleFileSelect}
                />
              </div>
            </div>
          )}

          {/* State B - Company has no logo */}
          {!companyLogoUrl && !customLogo && !isCleared && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200">
                <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p>No logo has been set for {companyName}.</p>
                  <a
                    href="/settings/company"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-900 underline hover:no-underline"
                  >
                    Add one in Company Settings →
                  </a>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">
                  Or upload a logo for this template only
                </Label>
                <UploadComponent
                  isDragging={isDragging}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onFileSelect={handleFileSelect}
                />
                <p className="text-xs text-muted-foreground">
                  This logo will only be used in this template. To apply it
                  across all templates for this company, add it in Company
                  Settings.
                </p>
              </div>
            </div>
          )}

          {/* Override State - Custom logo uploaded */}
          {customLogo && (
            <div className="space-y-3">
              <div className="relative border rounded-lg p-6 bg-white flex flex-col items-center justify-center">
                <button
                  onClick={handleClearLogo}
                  className="absolute top-2 right-2 h-6 w-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  title="Clear logo"
                >
                  <X className="h-4 w-4" />
                </button>
                <img
                  src={customLogo}
                  alt="Custom logo"
                  className="max-h-20 max-w-60 object-contain"
                />
                <p className="text-xs text-muted-foreground mt-3">
                  Custom logo — this template only
                </p>
              </div>
              {companyLogoUrl && (
                <button
                  onClick={handleRevertToCompanyLogo}
                  className="text-sm text-primary hover:underline"
                >
                  Revert to company logo →
                </button>
              )}
            </div>
          )}

          {/* Show upload when "Use different logo" is clicked */}
          {showUpload && companyLogoUrl && !customLogo && (
            <div className="space-y-2">
              <Label className="text-sm">Upload a different logo</Label>
              <UploadComponent
                isDragging={isDragging}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onFileSelect={handleFileSelect}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Panel 2: Logo Size */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Logo Size</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ToggleGroup
            type="single"
            value={logoSize}
            onValueChange={(value) => {
              if (value) onSizeChange(value as 'small' | 'medium' | 'large');
            }}
            className="justify-start"
          >
            <ToggleGroupItem value="small" aria-label="Small size">
              Small
            </ToggleGroupItem>
            <ToggleGroupItem value="medium" aria-label="Medium size">
              Medium
            </ToggleGroupItem>
            <ToggleGroupItem value="large" aria-label="Large size">
              Large
            </ToggleGroupItem>
          </ToggleGroup>
        </CardContent>
      </Card>

      {/* Panel 3: Primary Brand Colour */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">
            Primary Brand Colour
          </CardTitle>
          <CardDescription>
            Used for headings and accents in your document
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => onPrimaryColorChange(e.target.value)}
                className="absolute inset-0 w-12 h-12 opacity-0 cursor-pointer"
                title="Click to open color picker"
              />
              <div
                className="w-12 h-12 rounded border-2 border-gray-300 pointer-events-none shadow-sm"
                style={{ backgroundColor: primaryColor }}
              />
            </div>
            <div className="flex-1">
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                    onPrimaryColorChange(value);
                  }
                }}
                placeholder="#1a5c5c"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-base"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Panel 4: Document Font */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Document Font</CardTitle>
          <CardDescription>
            Applied to all text in your document
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={documentFont} onValueChange={onDocumentFontChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a font" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Arial">Arial</SelectItem>
              <SelectItem value="Calibri">Calibri</SelectItem>
              <SelectItem value="Georgia">Georgia</SelectItem>
              <SelectItem value="Helvetica">Helvetica</SelectItem>
              <SelectItem value="Times New Roman">Times New Roman</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
    </div>
  );
}

interface UploadComponentProps {
  isDragging: boolean;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function UploadComponent({
  isDragging,
  onDrop,
  onDragOver,
  onDragLeave,
  onFileSelect,
}: Readonly<UploadComponentProps>) {
  return (
    <button
      type="button"
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onClick={() => document.getElementById('logo-upload')?.click()}
      className={`
        w-full border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
        transition-colors bg-transparent
        ${isDragging ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'}
      `}
    >
      <input
        type="file"
        id="logo-upload"
        className="hidden"
        accept=".png,.jpg,.jpeg"
        onChange={onFileSelect}
      />
      <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
      <p className="text-sm font-medium">
        Drop your logo here, or <span className="text-primary">browse</span>
      </p>
      <p className="text-xs text-muted-foreground mt-1">
        PNG or JPG (max 2MB)
      </p>
    </button>
  );
}
